import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {ElementDataType} from '@Libraries/add-media-library';
import {ImageItemSource} from '@Libraries/image-item.library';
import {getUrlForGeneratedAiChatModelAssetsImage} from '@Libraries/ai-chat.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {loadImageAsync} from '@Utils/image.util';
import {getContainCenteredPlacement} from '@Utils/math.util';
import {getUniqueId} from '@Utils/string.util';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {LoadingVariant} from '@Libraries/loading-toast.types';
import {replacePosterLoadUrl} from '@Libraries/poster-editor.library';

const ADD_AI_CHAT_IMAGE_LOADING_KEY = 'addAiChatImageToEditor';

/**
 * Adds one image (from a URL) as a contain-centered item to the poster's first page — but only while that
 * page is still empty, so we never stack onto real content. The empty-page guard lives here so both callers
 * share it; no-op when the page is missing or already has items.
 */
const addImageUrlToEmptyFirstPage = async (poster: Poster, imageUrl: string, aiChatImageAssetHashedId?: string): Promise<void> => {
  const pageId = poster.pages.pageOrder[0];
  const page = pageId ? poster.pages.getPage(pageId) : undefined;
  if (!page || Object.keys(page.items.itemsHashMap).length > 0) {
    return;
  }

  const image = await loadImageAsync(imageUrl);
  const placement = getContainCenteredPlacement(page.poster.width, page.poster.height, image.naturalWidth, image.naturalHeight);

  await page.items.addItems.addNewItems(
    [
      {
        type: ElementDataType.IMAGE,
        source: ImageItemSource.UPLOAD,
        uid: getUniqueId(),
        dataUrl: imageUrl,
        ...(aiChatImageAssetHashedId ? {aiChatImageAssetHashedId} : {}),
        ...placement,
      },
    ],
    {selectOnAdd: false}
  );
};

export const addAiChatImageToEditor = async (poster: Poster, aiChatImageAssetHashedId: string): Promise<void> => {
  replacePosterLoadUrl(poster.hashedID);

  showLoading(ADD_AI_CHAT_IMAGE_LOADING_KEY, {text: window.i18next.t('pmwjs_preparing_editor'), variant: LoadingVariant.AI});
  try {
    const assetUrl = getUrlForGeneratedAiChatModelAssetsImage(aiChatImageAssetHashedId, MediaItemSizeType.HIGH_RES);
    await addImageUrlToEmptyFirstPage(poster, assetUrl, aiChatImageAssetHashedId);
  } finally {
    hideLoading(ADD_AI_CHAT_IMAGE_LOADING_KEY);
  }
};

/**
 * Editable-layers failure fallback: drops the user's original image onto the still-blank boot poster so a
 * failed "Editable layers" conversion leaves the image in the editor instead of an empty canvas. Module-level entry
 * helper — resolves the current poster from the editor global and swallows its own errors so it can never mask
 * the failure growl the caller shows next.
 */
export const loadOriginalImageIntoEditorOnLayersFailure = async (imageUrl: string): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }

  try {
    await addImageUrlToEmptyFirstPage(poster, imageUrl);
  } catch (e: unknown) {
    console.error(e);
  }
};
